// ReportDetail.jsx — v161.22.25
//
// Read-only view of a submitted bug report. Shows every entry, the auto-fix
// progress state, and the reply thread Claude posts during the fix run.
//
// The reply thread (added v161.22.25) is the loop-closing piece: when
// AgentMac claims the job, distributes a fix, retries, or gives up, the
// backend inserts rows in dist_bug_replies which we render below the
// fix-progress card. Polling both endpoints in parallel so the timeline
// stays in sync without a WS dependency.

import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { bugReports } from '../lib/api.js';

const FIX_STATE_LABEL = {
  queued:        '⏳ Queued',
  claimed:       '🔧 Mac picked it up',
  attempted:     '🛠 Fix attempted',
  fix_confirmed: '✅ Fixed',
  fix_failed:    '❌ Fix failed',
  cancelled:     'Cancelled'
};

// Visual styles for each reply kind. Status = neutral progress note,
// summary = end-of-run wrap-up (positive), question = needs tester
// input, error = give-up state requiring human review.
const REPLY_KIND_STYLE = {
  status:   { icon: '💬', color: '#94a3b8', bg: 'rgba(148,163,184,0.10)', label: 'Update' },
  summary:  { icon: '✅', color: '#10b981', bg: 'rgba(16,185,129,0.12)',  label: 'Summary' },
  question: { icon: '❓', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  label: 'Question' },
  error:    { icon: '⚠️', color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   label: 'Needs review' }
};

export default function ReportDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [replies, setReplies] = useState([]);
  // v161.22.31: attempt history for the chain. Only relevant when the
  // report has gone through at least one attempt (any state past 'submitted').
  const [attemptHistory, setAttemptHistory] = useState(null);
  const [error, setError] = useState('');
  // v161.22.32 (audit fix): real refetch trigger. Mutating `data` to
  // include a `_refetch: Date.now()` field (the old approach) did NOT
  // actually trigger the polling useEffect — that effect depends only
  // on `[id]`. Now we increment refetchTrigger and include it in the
  // dep array, so any caller that wants a fresh fetch just bumps it.
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Stash latest fix state in a ref so the polling loop reads fresh values
  // instead of the closure capture from the first tick (which was the bug
  // in v22.24 — `data` was always the initial null in the next-tick delay
  // computation).
  const latestFixStateRef = useRef(null);

  useEffect(() => {
    let alive = true;
    let timer;

    async function tick() {
      try {
        // Fire fetches in parallel — reply count is small and the
        // backend already has indexes on report_id, so the join is cheap.
        const [r, rep, hist] = await Promise.all([
          bugReports.get(id),
          bugReports.replies(id),
          bugReports.attemptHistory(id).catch(() => null)  // best-effort
        ]);
        if (!alive) return;
        setData(r);
        setReplies(rep.replies || []);
        if (hist) setAttemptHistory(hist);
        latestFixStateRef.current = r.fixState?.state || null;
      } catch (e) {
        if (alive) setError(e.message);
      }
      // Poll fast while the fix is in flight; back off when terminal.
      const s = latestFixStateRef.current;
      const live = s && ['queued', 'claimed', 'attempted'].includes(s);
      timer = setTimeout(tick, live ? 5000 : 30000);
    }
    tick();
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refetchTrigger]);

  if (error && !data) return <div className="error">Error: {error}</div>;
  if (!data) return <div className="loading">Loading…</div>;

  const { report, entries, fixState } = data;
  const fixLabel = fixState?.state && (FIX_STATE_LABEL[fixState.state] || fixState.state);

  // v161.22.28: split replies by which entry they target. report-level
  // replies (entry_id=null) render in the main thread; entry-level
  // replies render under that specific entry's card.
  const reportLevelReplies = replies.filter(r => !r.entry_id);
  const repliesByEntry = new Map();
  for (const r of replies) {
    if (!r.entry_id) continue;
    if (!repliesByEntry.has(r.entry_id)) repliesByEntry.set(r.entry_id, []);
    repliesByEntry.get(r.entry_id).push(r);
  }

  async function handleArchive() {
    if (!confirm('Archive this report? It stays in your history but is hidden from the active list.')) return;
    try {
      await bugReports.archive(id);
      // v161.22.32 (audit fix): trigger a refetch via the dependency
      // counter instead of clearing data (which flashed a loading state).
      setRefetchTrigger(c => c + 1);
    } catch (e) {
      alert(e.message);
    }
  }

  // v161.22.30: tester insists the likely-duplicate verdict is wrong
  // and wants the auto-fix run anyway. Backend creates the fix request
  // and flips the report to 'submitted'.
  async function handleOverrideLikelyDuplicate() {
    if (!confirm(
      'Override the duplicate detection?\n\n' +
      'Make sure you\'ve installed the most recent build and confirmed the bug is still happening. ' +
      'Auto-fix is expensive — only run it when you\'re sure.'
    )) return;
    try {
      await bugReports.overrideLikelyDuplicate(id);
      setRefetchTrigger(c => c + 1);
    } catch (e) {
      alert(e.message);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>{report.title || 'Untitled session report'}</h1>
          <p className="muted">
            {report.app_slug}
            {report.app_version && ` · v${report.app_version}`}
            {report.device_model && ` · ${report.device_model}`}
            {report.ios_version && ` · iOS ${report.ios_version}`}
            {' · '}{entries.length} {entries.length === 1 ? 'bug' : 'bugs'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="badge badge-info">{report.state}</span>
          {/* v161.22.32 (audit fix): archive button shown for any
              archivable state (matches backend's expanded allow-list). */}
          {['fixed', 'submitted', 'fixing', 'reopened',
            'needs_human_review', 'likely_duplicate'].includes(report.state) && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleArchive}
              title="Archive this report"
            >
              Archive
            </button>
          )}
        </div>
      </header>

      {/* v161.22.30: likely-duplicate banner. Shown only when the
          report's state is 'likely_duplicate'. The button overrides the
          decision and triggers an auto-fix run. */}
      {report.state === 'likely_duplicate' && (
        <div className="likely-dup-card">
          <h3>⚠️ This looks like a bug we already fixed</h3>
          <p>
            We didn't queue an auto-fix run because this report matches a bug
            that was recently fixed in another build. Try installing the most
            recent build of this app first, then test whether the bug is still
            happening.
          </p>
          <p className="muted" style={{ fontSize: 13 }}>
            If the bug really is still there after the latest build, tap below
            to override and run auto-fix anyway. This will cost real Claude
            time, so only do it if you've verified.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={handleOverrideLikelyDuplicate}
            >
              Run auto-fix anyway
            </button>
            <button
              className="btn btn-text"
              onClick={handleArchive}
            >
              Archive this report
            </button>
          </div>
        </div>
      )}

      {/* v161.22.31: needs_human_review banner. Shown when the regression
          retry cap was hit and auto-fix has given up. The dev needs to
          step in — replying on the thread is the way to share more context. */}
      {report.state === 'needs_human_review' && (
        <div className="needs-review-card">
          <h3>🚨 Auto-fix gave up after multiple attempts</h3>
          <p>
            This bug has been through {(attemptHistory?.attempts.length || 0)} fix attempts
            and the regression keeps coming back. Auto-fix has stopped trying
            so we don't keep burning Claude time on the same dead end.
          </p>
          <p className="muted" style={{ fontSize: 13 }}>
            The developer will pick this up manually. If you have additional
            context (specific reproduction steps, screenshots from the latest
            build, a hypothesis about what's going wrong) — reply on the
            thread below. That gives the human reviewer something to start with.
          </p>
        </div>
      )}

      {/* v161.22.31: attempt history. Visible whenever the report has
          been through one or more fix attempts. Compact for single-attempt
          reports (just labels the build); expansive for retry chains. */}
      {attemptHistory && attemptHistory.attempts.length > 0 && (
        <AttemptHistory history={attemptHistory} currentState={report.state} />
      )}

      {fixState && (
        <div className="fix-progress-card">
          <h3>Auto-Fix Progress</h3>
          <p>{fixLabel}</p>
          {report.fix_request_id && (
            <Link to={`/fix-progress/${report.fix_request_id}`} className="btn btn-secondary">
              View live progress →
            </Link>
          )}
        </div>
      )}

      <ReplyThread replies={reportLevelReplies} fixState={fixState?.state} />

      <h3 style={{ marginTop: 24 }}>Bug entries</h3>
      <ol className="entry-list">
        {entries.map((e, idx) => (
          <li key={e.id} className="entry-card">
            <div className="entry-header">
              <span className="entry-index">#{idx + 1}</span>
              <strong className="entry-title">{e.title}</strong>
              <span className="badge badge-sm">{e.severity}</span>
            </div>
            <div className="entry-body">
              {e.steps?.length > 0 && (
                <>
                  <strong>Steps</strong>
                  <ol className="entry-steps">
                    {e.steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </>
              )}
              {e.observed && <p><strong>Observed:</strong> {e.observed}</p>}
              {e.expected && <p><strong>Expected:</strong> {e.expected}</p>}
              {e.notes && <p><strong>Notes:</strong> {e.notes}</p>}
              <p className="muted">
                Reproduces: {e.reproduces}
                {e.hasScreenshot && ' · 📷 screenshot'}
                {e.hasRecording && ' · 🎥 recording'}
                {e.hasPdf && (
                  <> · <a
                    href={`/api/v1/dist/me/bug-reports/${id}/entries/${e.id}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--accent)' }}
                  >📄 PDF report</a></>
                )}
              </p>
              {/* v161.22.28: per-entry replies thread, when present. */}
              {repliesByEntry.has(e.id) && (
                <EntryReplyThread replies={repliesByEntry.get(e.id)} />
              )}
              {/* v161.22.26: per-entry verification. Show buttons once the
                  fix has been distributed; show badge once verified. */}
              <EntryVerification
                entry={e}
                reportId={id}
                onVerified={() => {
                  /* v161.22.32 (audit fix): trigger a real refetch.
                     Previously this mutated `data` with a `_refetch`
                     timestamp, which didn't change the useEffect deps
                     and so didn't actually refetch — the badge stayed
                     stale until the next 5-30s poll tick. */
                  setRefetchTrigger(c => c + 1);
                }}
              />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ── Reply thread ───────────────────────────────────────────────────────
//
// Shows Claude's auto-replies in chronological order. While the fix is
// live (queued/claimed/attempted) we also render a thin "thinking…" hint
// underneath the last status reply so the tester knows the polling is
// active and more updates may arrive.

function ReplyThread({ replies, fixState }) {
  const live = fixState && ['queued', 'claimed', 'attempted'].includes(fixState);

  if (!replies.length) {
    if (live) {
      return (
        <div className="reply-thread reply-thread-empty">
          <div className="reply-pending">
            <span className="dot" /> Waiting for Claude to pick up your report…
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="reply-thread">
      <h3>Updates from Claude</h3>
      <ol className="reply-list">
        {replies.map(r => {
          const style = REPLY_KIND_STYLE[r.kind] || REPLY_KIND_STYLE.status;
          return (
            <li
              key={r.id}
              className="reply-item"
              style={{ borderLeft: `3px solid ${style.color}`, background: style.bg }}
            >
              <div className="reply-header">
                <span style={{ color: style.color }}>{style.icon} {style.label}</span>
                <span className="muted reply-time">{relativeTime(r.created_at)}</span>
              </div>
              <p className="reply-body">{r.body}</p>
              {r.payload?.outputBuildId && (
                <p className="muted reply-meta">
                  Build delivered. Reinstall from the Apps list to verify.
                </p>
              )}
            </li>
          );
        })}
      </ol>
      {live && (
        <div className="reply-pending">
          <span className="dot" /> Claude is still working — more updates may arrive.
        </div>
      )}
    </div>
  );
}

// ── AttemptHistory ─────────────────────────────────────────────────────
//
// v161.22.31: chronological list of fix attempts on this report's chain.
// Compact when there's just one attempt (common case — most reports get
// fixed on the first try). Expansive when regression retries kick in, so
// the tester can see "Attempt 1 (build 47) failed → Attempt 2 (build 51)
// failed → Attempt 3 …" and reason about whether to keep waiting or
// chase the dev.

function AttemptHistory({ history, currentState }) {
  if (!history?.attempts?.length) return null;

  // Only show the section when there's something interesting to display:
  // multiple attempts, OR a single attempt that failed (regression /
  // gate failure). A single successful attempt doesn't warrant a section.
  const showHistory = history.attempts.length > 1
    || currentState === 'needs_human_review'
    || history.attempts.some(a => a.isRegressionRetry);
  if (!showHistory) return null;

  return (
    <div className="attempt-history">
      <h3>Fix attempts ({history.attempts.length})</h3>
      <ol className="attempt-list">
        {history.attempts.map(a => {
          const stateColor = {
            distributed: 'var(--success)',
            failed: 'var(--danger)',
            gates_failed: 'var(--danger)',
            attempted: 'var(--warning)',
            claimed: 'var(--warning)',
            queued: 'var(--text-muted)'
          }[a.state] || 'var(--text-muted)';

          // Per-entry verdict counts inside this attempt
          const confirmed = a.entries.filter(e => e.state === 'fix_confirmed').length;
          const regressed = a.entries.filter(e => e.state === 'regressed').length;
          const stillPending = a.entries.filter(e => !['fix_confirmed', 'regressed'].includes(e.state)).length;

          return (
            <li key={a.attemptId} className="attempt-card">
              <div className="attempt-header">
                <span className="attempt-num">
                  Attempt {a.attemptNumber}
                  {a.isRegressionRetry && (
                    <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                      (retry of failed fix)
                    </span>
                  )}
                </span>
                <span
                  className="badge badge-sm"
                  style={{ color: stateColor, background: 'transparent', border: `1px solid ${stateColor}` }}
                >
                  {a.state}
                </span>
              </div>
              <div className="attempt-meta muted">
                {a.outputBuildLabel && <>Build {a.outputBuildLabel}</>}
                {a.diff?.filesChanged > 0 && (
                  <> · {a.diff.filesChanged} file(s), +{a.diff.linesAdded} / −{a.diff.linesDeleted}</>
                )}
                {a.startedAt && <> · started {relativeTime(a.startedAt)}</>}
              </div>
              {a.gateFailureReason && (
                <p className="attempt-detail" style={{ color: 'var(--danger)' }}>
                  Gate failure: {a.gateFailureReason}
                </p>
              )}
              {a.entries.length > 0 && (
                <p className="attempt-detail muted">
                  Tester verdict:
                  {confirmed > 0 && <> ✓ {confirmed} confirmed</>}
                  {regressed > 0 && <> ✗ {regressed} still broken</>}
                  {stillPending > 0 && <> ? {stillPending} not yet verified</>}
                </p>
              )}
              {a.entries.flatMap(e => e.regressionNotes).slice(0, 2).map((n, i) => (
                <p key={i} className="attempt-detail" style={{ fontStyle: 'italic' }}>
                  Tester note: "{n.note}"
                </p>
              ))}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ── EntryReplyThread ───────────────────────────────────────────────────
//
// v161.22.28: compact reply thread rendered inside a specific entry card.
// Same visual conventions as the top-level ReplyThread but smaller (no
// header, tighter spacing) since it's nested.

function EntryReplyThread({ replies }) {
  if (!replies || !replies.length) return null;
  return (
    <div className="entry-replies">
      <div className="entry-replies-label">Replies on this bug</div>
      <ol className="reply-list reply-list-compact">
        {replies.map(r => {
          const style = REPLY_KIND_STYLE[r.kind] || REPLY_KIND_STYLE.status;
          const authorLabel = r.author_kind === 'admin'
            ? '👤 Developer'
            : r.author_kind === 'system'
            ? '⚙️ System'
            : `${style.icon} ${style.label}`;
          return (
            <li
              key={r.id}
              className="reply-item reply-item-compact"
              style={{ borderLeft: `3px solid ${style.color}`, background: style.bg }}
            >
              <div className="reply-header">
                <span style={{ color: style.color }}>{authorLabel}</span>
                <span className="muted reply-time">{relativeTime(r.created_at)}</span>
              </div>
              <p className="reply-body">{r.body}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ── EntryVerification ──────────────────────────────────────────────────
//
// v161.22.26: per-bug verify/regress UI on the report detail page.
//
// Shown only when entry.fixRequestState === 'fix_attempted' — i.e., a fix
// has been distributed but the tester hasn't confirmed whether it worked.
//
// Once verified, the box flips to a static "✓ verified" or "⚠️ still
// broken" badge with the optional note the tester left.

function EntryVerification({ entry, reportId, onVerified }) {
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [error, setError] = useState('');

  // Static badge for terminal states (already verified or regressed).
  if (entry.fixRequestState === 'fix_confirmed') {
    return (
      <div className="verify-result verify-ok">
        ✅ You verified this bug is fixed.
      </div>
    );
  }
  if (entry.fixRequestState === 'regressed') {
    return (
      <div className="verify-result verify-bad">
        ⚠️ You reported this bug is still broken.
      </div>
    );
  }
  if (entry.fixRequestState === 'fix_failed') {
    return (
      <div className="verify-result verify-bad">
        ❌ Auto-fix failed for this bug. The dev will look at it manually.
      </div>
    );
  }

  // Pre-fix states: nothing actionable yet.
  if (entry.fixRequestState !== 'fix_attempted') return null;

  async function handle(passed) {
    setSubmitting(true);
    setError('');
    try {
      await bugReports.verifyEntry(reportId, entry.id, passed, note.trim() || null);
      onVerified && onVerified();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="verify-prompt">
      <strong>A candidate fix has shipped — does it work?</strong>
      {showNote && (
        <textarea
          className="verify-note"
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={2}
          placeholder="Optional: describe what you saw"
        />
      )}
      <div className="verify-buttons">
        <button
          className="btn btn-success btn-sm"
          onClick={() => handle(true)}
          disabled={submitting}
        >
          ✓ Fixed
        </button>
        <button
          className="btn btn-danger btn-sm"
          onClick={() => handle(false)}
          disabled={submitting}
        >
          ✗ Still broken
        </button>
        <button
          className="btn btn-text btn-sm"
          onClick={() => setShowNote(s => !s)}
        >
          {showNote ? '− Hide note' : '+ Add note'}
        </button>
      </div>
      {error && <div className="error">{error}</div>}
    </div>
  );
}
